import { AggregatedTargetAttribute, Device, Facility, TrafficReportConfiguration } from "@app/shared";
import { zonedTimeToUtc } from "date-fns-tz";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFacilityTZ } from "../../hooks/useFacilityTZ";
import { RequiresGoogleMaps } from "../shared/maps/RequiresGoogleMaps";
import { PrintReportContainer } from "./PrintReportContainer";
import { DefaultReport, GetReport, SelectedReportConfiguration } from "./ReportComponents";
import { AllTargetTypes, ReportContext } from "./reportHelpers";
import { getReportPeriod, ReportParametersForm } from "./ReportParametersForm";


type ReportsViewProps = {
    facility?: Facility;
    device?: Device;
    initialReportKey?: string;
    onChange: () => any;
};

const lastViewedReportIdKey = "lastViewedReportIdv1";
const reportStateKeyPrefix = "lastViewedReportStatev2:";
const reportStateKeyFor = (reportId: string) => `${reportStateKeyPrefix}${reportId}`;

export const ReportsView: React.FC<ReportsViewProps> = ({ facility, device, initialReportKey }) => {

    const [facilityTZ, , { isLoading: isFacilityTZLoading }] = useFacilityTZ(facility ?? device?.facilityId!, true);
    const lastViewedReportId = initialReportKey?.length ? undefined : window.sessionStorage.getItem(lastViewedReportIdKey) ?? undefined;
    const defaultReport = GetReport(initialReportKey ?? lastViewedReportId ?? DefaultReport.key);
    const [searchParams, setSearchParams] = useState<TrafficReportConfiguration>()
    const [reportIsLoading, setIsLoading] = useState(false);
    const [reportConfig, setReportConfig] = useState<SelectedReportConfiguration>();

    const isLoading = useMemo(() => (isFacilityTZLoading || reportIsLoading) ?? false, [isFacilityTZLoading, reportIsLoading]);

    const reportConfigRef = useRef<SelectedReportConfiguration | undefined>(undefined);
    reportConfigRef.current = reportConfig;

    const childSetSearchParams = useCallback((params: SelectedReportConfiguration) => {
        const newRC = { ...reportConfigRef.current, ...params } as SelectedReportConfiguration;
        setReportConfig(newRC);
        setSearchParams(curr => haveChanged(curr, newRC)
            ? ({ ...curr, ...getSearchParams(newRC) } as TrafficReportConfiguration)
            : curr);
        window.sessionStorage.setItem(lastViewedReportIdKey, newRC.selectedReportId);
        window.sessionStorage.setItem(reportStateKeyFor(newRC.selectedReportId), JSON.stringify({ ...getSearchParams(newRC), selectedReportId: newRC.selectedReportId, chartTimezone: newRC.chartTimezone, chartType: newRC.chartType, selectedDateId: newRC.selectedDateId, inclusiveStart: newRC.inclusiveStart, exclusiveEnd: newRC.exclusiveEnd }));
    }, []);


    useEffect(() => {
        if (reportConfig || isLoading) { return; }
        let c = {
            selectedReportId: defaultReport.key,
            chartTimezone: facilityTZ,
            selectedDateId: defaultReport.defaultRange,
            chartType: defaultReport.defaultChartType,
            grouping: (initialReportKey?.length ?? 0) > 0 ? AggregatedTargetAttribute.None : AggregatedTargetAttribute.TargetType,
            groupingValues: AllTargetTypes as number[],
            deviceIds: facility?.devices?.length ? facility.devices.map(d => d.id!) : undefined,
            ...getReportPeriod(defaultReport.defaultRange, facilityTZ ?? 'UTC'),
            context: { device: device, facility: facility, setReportConfig: childSetSearchParams }
        };
        const lastViewedConfig = window.sessionStorage.getItem(reportStateKeyFor(defaultReport.key));
        if (!initialReportKey?.length && lastViewedConfig?.length) {
            const sessionConfig = new TrafficReportConfiguration(JSON.parse(lastViewedConfig) as any);
            if (sessionConfig.deviceIds?.every(sessionDev => (c.context.facility?.devices?.find(x => (x.id!) === sessionDev)) || (c.context.device?.id!) === sessionDev)) {// session should be limited to same devices otherwise zones may not line up corrupting periods
                c = { ...c, ...(sessionConfig) };
                const devIds = [] as Array<string>;

                if (c.exclusiveEnd?.toString() === 'Invalid Date')
                    c.exclusiveEnd = undefined;

                if (c.inclusiveStart?.toString() === 'Invalid Date')
                    c.inclusiveStart = undefined;

                c.deviceIds?.forEach(dId => {
                    if (facility && facility.devices?.find(d => d.id === dId)) { devIds.push(dId!); }
                    else if (device?.id === dId) { devIds.push(dId!); }
                });
                c.deviceIds = devIds;
            }
        }
        setReportConfig(c as unknown as SelectedReportConfiguration);
    }, [defaultReport, device, facility, facilityTZ, isLoading, childSetSearchParams, reportConfig])


    const dataSourceName = useMemo(() => {
        if (device) { return device.name; }
        if (reportConfig?.deviceIds?.length && (facility?.devices?.length ?? 0) > reportConfig.deviceIds.length) {
            const names = reportConfig.deviceIds.map(dId => (facility?.devices?.find(d => d.id === dId)?.name)).join(', ');
            return reportConfig.deviceIds?.length > 1 ? names.substring(0, names.length - 2) : names;
        }
        return facility!.name;
    }, [facility, device, reportConfig]);



    useEffect(() => {
        if (isLoading || !!!facilityTZ || !reportConfig) return;
        if (!!!searchParams || facilityTZ !== reportConfig.chartTimezone) {
            childSetSearchParams({ ...reportConfig, chartTimezone: facilityTZ });
        }
    }, [isLoading, facilityTZ, childSetSearchParams, reportConfig, searchParams]);

    // A location with no devices has no data to report on, so a report here has no value
    // (and the API rejects it). Guide the user to add a device instead of running it.
    if (!!!device && facility && !(facility.devices?.length)) {
        return (<div className="flex items-center justify-center h-screen"><h1>This location has no devices yet. Add a device to this location to run reports.</h1></div>);
    }

    if (!searchParams || !reportConfig || (!!!device && !!!facility)) {
        if (!!!facility && !!!device?.facilityId) {
            return (<div className="flex items-center justify-center h-screen"><h1>Reports for devices without Locations are not currently supported</h1></div>);
        }
        return null;
    }

    return (
        <RequiresGoogleMaps>
            <div className="flex flex-col">
                <ReportContext.Provider value={{ isLoading, setIsLoading, reportConfig: reportConfig, searchParams: searchParams, updateReportConfig: childSetSearchParams }} >
                    <ReportParametersForm />
                    <PrintReportContainer dataSource={dataSourceName} />
                </ReportContext.Provider>
            </div >
        </RequiresGoogleMaps>
    );
};

function arrayEq<T>(a: T[] | undefined, b: T[] | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

function dateEq(a: Date | string | undefined, b: Date | string | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    const av = a instanceof Date ? a.getTime() : new Date(a).getTime();
    const bv = b instanceof Date ? b.getTime() : new Date(b).getTime();
    return av === bv;
}

function haveChanged(searchParams: TrafficReportConfiguration | undefined, reportConfig: SelectedReportConfiguration): boolean {
    if (!searchParams) return true;
    return !arrayEq(reportConfig.deviceIds, searchParams.deviceIds)
        || !arrayEq(reportConfig.facilityIds, searchParams.facilityIds)
        || !dateEq(reportConfig.exclusiveEnd, searchParams.exclusiveEnd)
        || !dateEq(reportConfig.inclusiveStart, searchParams.inclusiveStart)
        || reportConfig.grouping !== searchParams.grouping
        || !arrayEq(reportConfig.groupingValues, searchParams.groupingValues);
}

function getSearchParams(cfg: SelectedReportConfiguration): TrafficReportConfiguration {
    const trc = {} as TrafficReportConfiguration;
    for (const k in (new TrafficReportConfiguration())) {
        (trc as any)[k] = (cfg as any)[k as any];
    };
    if (cfg.context.device) {
        if (!cfg.deviceIds?.length) {
            trc.deviceIds = [cfg.context.device.id!];
        }
    } else {
        trc.facilityIds = [cfg.context.facility?.id!];
    }
    if (!cfg.inclusiveStart) {
        trc.inclusiveStart = zonedTimeToUtc('2020-01-01T00:00:00', cfg.chartTimezone);
    }
    if (!cfg.exclusiveEnd) {
        trc.exclusiveEnd = zonedTimeToUtc(new Date().getFullYear() + 1 + '-01-01T00:00:00', cfg.chartTimezone);
    }
    return trc;
}